/**
 * The New Campaign screen (PLAN §12.2, specification §7–§16).
 *
 * The largest screen, and the one that reuses the most: all three input modes,
 * the segment counters, the list review and the verdict come from `domain/` and
 * from the four `web/*-view.ts` modules unchanged. What is written here is the
 * drawing and the event wiring — the decisions were already made, and made in
 * modules that are tested without a browser.
 *
 * **The verdict is reached twice, and this is the convenient half.**
 * `checkCampaign` runs on every keystroke so Continue is accurate while the
 * screen is being filled in; the `campaign` Edge Function runs the same
 * function on the same input and its answer is the one that counts. This copy
 * can be skipped — by an old bundle, by a broken deploy — so it is never
 * trusted, only helpful.
 *
 * **Nothing somebody can be typing into is rebuilt, and nothing is moved.**
 * The textareas, the separator, the file chooser and the tab strip are all
 * built once, and each mode's panel is a node that stays put until the mode
 * changes. Moving is as bad as rebuilding: `appendChild` moves a node rather
 * than copying it, so a redraw that re-inserts a focused textarea takes the
 * focus off it — which is what made this screen accept exactly one character
 * and then look frozen. A keystroke redraws counters, findings and previews,
 * and touches nothing else.
 */

import {
  EMPTY_INPUT,
  type CampaignInput,
  type InputMode,
} from '../../../domain/campaign-input.ts';
import { checkCampaign, type CampaignCheck } from '../../../domain/campaign-check.ts';
import { CAMPAIGN_PROBLEM_MESSAGES } from '../../../domain/campaign-problems.ts';
import { DEFAULT_MAX_ROWS, PROBLEM_MESSAGE_KEYS } from '../../../domain/validation.ts';
import {
  DUPLICATE_RESOLUTIONS,
  SEPARATORS,
  type DuplicateResolution,
  type SeparatorName,
} from '../../../domain/recipients.ts';
import { PROMO_PLACEHOLDER } from '../../../domain/template.ts';
import { blocksView, joinBlocks } from '../../blocks-view.ts';
import { combineImports, importView, readImported, writeImported } from '../../import-view.ts';
import { listView } from '../../list-view.ts';
import { templateView } from '../../template-view.ts';
import { readSpreadsheet } from '../../import.ts';
import { el, number, replace, type Child } from '../dom.ts';
import { WARN_IF_PARTS_ABOVE } from '../config.ts';
import { deleteTemplate, gate, GateRefused, saveTemplate, templates as savedTexts, type TemplateRow } from '../data.ts';
import { clearDraft, isEmpty, loadDraft, saveDraft } from '../../draft-store.ts';
import { translate as t, translateKey } from '../i18n.ts';
import { navigate, renderShell } from '../shell.ts';
import { BLOCK_WORDING, IMPORT_WORDING, type Wording } from './wording.ts';

const SEPARATOR_LABELS: Readonly<Record<SeparatorName, string>> = {
  semicolon: 'campaign.separator.semicolon',
  comma: 'campaign.separator.comma',
  tab: 'campaign.separator.tab',
  pipe: 'campaign.separator.pipe',
};

const MODE_LABELS: readonly (readonly [InputMode, string, string])[] = [
  ['pairs', 'campaign.mode.pairs', 'campaign.mode.pairs.hint'],
  ['blocks', 'campaign.mode.blocks', 'campaign.mode.blocks.hint'],
  ['file', 'campaign.mode.file', 'campaign.mode.file.hint'],
];

export function renderNewCampaign(root: Element, email: string, maxRows = DEFAULT_MAX_ROWS): void {
  let input: CampaignInput = { ...EMPTY_INPUT };

  // One key for this screen's list, generated before anything can be pressed.
  //
  // It is what makes Continue safe to press twice. The button disables itself
  // while the request is in flight, but that only covers the case where the
  // browser is the thing repeating: a refreshed POST, or a network layer
  // retrying underneath, does not ask the button. The key does — the gate hands
  // back the campaign it already wrote rather than writing a second one holding
  // the same ten thousand recipients.
  let key = crypto.randomUUID();

  // Built once. Everything else on the screen is redrawn from `input`.
  const pairs = el('textarea', { rows: 12, spellcheck: false, 'aria-label': t('campaign.pairs.label') });
  const template = el('textarea', { rows: 4, 'aria-label': t('campaign.template.label') });

  // Mode B's two boxes. **They are the state, and `input.blocks` is derived
  // from them** — the opposite of every other field on this screen, and worth
  // the exception.
  //
  // The obvious arrangement is the one used everywhere else: keep the string,
  // and put it back into the boxes on every redraw. It cannot be done here,
  // because splitting the joined string is not the inverse of joining it. A
  // blank line typed inside the left-hand box makes three blocks, and a split
  // on the first blank run would move everything after it into the right-hand
  // box — rewriting what somebody is in the middle of typing, and taking the
  // caret with it. So the boxes keep their own text, `joinBlocks` produces the
  // string the two verdicts are reached on, and nothing ever writes back except
  // the swap, which exchanges them whole.
  const phones = el('textarea', { rows: 14, spellcheck: false, 'aria-label': t('campaign.blocks.phonesLabel') });
  const codes = el('textarea', { rows: 14, spellcheck: false, 'aria-label': t('campaign.blocks.codesLabel') });


  const restored = el('div', {});
  const savedPanel = el('div', { class: 'saved-texts' });
  const modePanel = el('div', { class: 'mode-panel' });
  const templatePanel = el('div', { class: 'template-panel' });
  const reviewPanel = el('div', { class: 'review-panel' });
  const problems = el('div', { class: 'findings' });
  const continueButton = el('button', { type: 'button', disabled: true }, t('campaign.continue'));

  const set = (over: Partial<CampaignInput>) => {
    input = { ...input, ...over };
    // Every change, on this device only. There is no server to hold a
    // half-finished campaign — a draft does not exist until the gate has
    // written one — so the alternative to `localStorage` is losing twenty
    // minutes of work to a stray refresh.
    saveDraft(input);
    refresh();
  };

  // Both boxes report the same field, because the field is the two of them.
  const blocksChanged = () => set({ blocks: joinBlocks(phones.value, codes.value) });

  /**
   * The swap the reversed warning offers, done as a swap rather than as a
   * rewrite.
   *
   * `blocksView` also computes what the joined text *would* become, which is
   * what the one-textarea panel used. Exchanging the two boxes reaches the same
   * place without going through a parse and a re-join, so nothing somebody
   * typed — spacing, a trailing line — has to survive a round trip to come
   * back unchanged.
   */
  const swap = () => {
    const wasPhones = phones.value;
    phones.value = codes.value;
    codes.value = wasPhones;
    blocksChanged();
  };

  // After `set` and `swap`, because the strip and all three panels are wired to
  // them — and all four are built exactly once, which is the point.
  const { node: tabs, showMode } = modeTabs(set);
  const panels: Readonly<Record<InputMode, Panel>> = {
    pairs: pairsPanel(set, pairs),
    blocks: blocksPanel({ phones, codes, swap }),
    file: filePanel(set, maxRows),
  };

  const refresh = () => {
    // One check per redraw, handed to both panels: ten thousand rows validated
    // twice per keystroke is the difference between a responsive screen and a
    // sticky one.
    const check = checkCampaign(input, { maxRows });

    showMode(input.mode);

    // **The panel is swapped when the mode changes, and never otherwise.**
    //
    // It used to be rebuilt on every keystroke, and rebuilding it moved the box
    // being typed into: `appendChild` moves a node rather than copying it, and
    // a focused element that leaves the document does not get its focus back
    // when it returns. So the first character typed was the last one the screen
    // accepted, which from the outside looks exactly like a freeze.
    //
    // Building the textareas once was not enough on its own — the panel around
    // them has to stay put too. Each panel now owns its boxes and redraws only
    // what is derived from them, which is the same rule the tab strip follows.
    const panel = panels[input.mode];
    if (modePanel.firstChild !== panel.node) replace(modePanel, panel.node);
    panel.update(input);

    replace(templatePanel, ...templateCounters(input, check));
    replace(reviewPanel, ...review(input, set, check, maxRows));
    replace(problems, ...check.problems.map((problem) =>
      finding({ message: CAMPAIGN_PROBLEM_MESSAGES[problem.name], level: 'error' }, problem.params),
    ));
    continueButton.disabled = !check.ok;
  };

  pairs.addEventListener('input', () => set({ pairs: pairs.value }));
  template.addEventListener('input', () => set({ template: template.value }));
  phones.addEventListener('input', blocksChanged);
  codes.addEventListener('input', blocksChanged);

  continueButton.addEventListener('click', () => {
    continueButton.disabled = true;
    replace(problems);

    void gate({ action: 'create', key, input: { ...input, keep: [...input.keep] } })
      .then((answer) => {
        // It is in the database now, which is a better place than this browser.
        // Keeping it would offer to restore a campaign that already exists.
        clearDraft();
        navigate(`/campaign/${(answer as { campaignId: number }).campaignId}/confirm`);
      })
      .catch((cause: unknown) => {
        // The gate refused what this screen thought was fine. That is a
        // disagreement between two runs of the same function, so it is worth
        // showing verbatim rather than summarising.
        // A new key, because the list is about to be edited and the next
        // Continue is a different campaign. Keeping the old one would hand back
        // the refused draft — which does not exist — or, worse, an earlier one.
        key = crypto.randomUUID();

        const failure = cause instanceof GateRefused ? cause.failure : undefined;
        replace(
          problems,
          ...(failure?.problems ?? []).map((problem) =>
            finding({ message: CAMPAIGN_PROBLEM_MESSAGES[problem.name as never] ?? problem.name, level: 'error' }, problem.params),
          ),
          failure?.problems === undefined ? finding({ message: 'app.loadFailed', level: 'error' }, {}) : false,
        );
        continueButton.disabled = false;
      });
  });

  renderShell(
    root,
    { active: 'campaign.new', email, title: t('campaign.new.title') },
    el(
      'section',
      { class: 'card' },
      el('h1', {}, t('campaign.new.title')),
      el('p', { class: 'muted' }, t('campaign.new.intro')),
      restored,
      tabs,
      modePanel,
    ),
    el(
      'section',
      { class: 'card' },
      el('h2', {}, t('campaign.template.title')),
      el('p', { class: 'muted' }, t('campaign.template.help', { placeholder: PROMO_PLACEHOLDER })),
      template,
      templatePanel,
      savedPanel,
    ),
    el('section', { class: 'card review' }, el('h2', {}, t('campaign.review.title')), reviewPanel),
    el('section', { class: 'card' }, problems, el('div', { class: 'actions' }, continueButton)),
  );

  template.placeholder = t('campaign.template.example', { placeholder: PROMO_PLACEHOLDER });

  /**
   * What was being typed here last time, if anything.
   *
   * **Offered, not imposed.** Silently filling a screen with text somebody does
   * not remember writing is worse than losing it: they cannot tell what is
   * theirs, and the one thing this screen must never do is send a list nobody
   * meant. So the banner says what happened and offers the way out.
   */
  const previous = loadDraft();
  if (previous !== undefined) {
    input = previous;
    // The boxes are the state for two of the three modes, so restoring the
    // object is not enough — the text has to go back where it was typed.
    pairs.value = previous.pairs;
    template.value = previous.template;
    const [restoredPhones, ...restoredCodes] = previous.blocks.split(/\n\s*\n/);
    phones.value = restoredPhones ?? '';
    codes.value = restoredCodes.join('\n\n');

    replace(
      restored,
      el(
        'div',
        { class: 'finding', role: 'status' },
        el('span', {}, t('draft.restored')),
        el(
          'button',
          {
            type: 'button',
            class: 'secondary',
            onclick: () => {
              clearDraft();
              window.location.reload();
            },
          },
          t('draft.discard'),
        ),
      ),
    );
  }

  savedTexts()
    .then((rows) => showSavedTexts(savedPanel, rows, () => template.value, (body) => {
      // Through `set`, so the counters, the findings and the autosave all see
      // it — assigning `template.value` alone would leave the screen showing
      // the old segment count for the new text.
      template.value = body;
      set({ template: body });
    }))
    .catch(() => undefined);

  refresh();
}

// ---------------------------------------------------------------------------
// Saved texts

/**
 * The same promo wording, kept once instead of retyped every month.
 *
 * Retyping is where a `%promo%` gets dropped, and a template without the
 * placeholder is a campaign that sends the same message to everybody — which
 * `checkCampaign` catches, but only after somebody has typed the whole thing
 * again.
 *
 * Shared across the team rather than per person: a handful of administrators
 * share one panel and already see every campaign each other sends, so a text
 * one of them saved is exactly what the next one wants.
 */
function showSavedTexts(
  panel: Element,
  rows: readonly TemplateRow[],
  currentBody: () => string,
  use: (body: string) => void,
): void {
  const name = el('input', { type: 'text', placeholder: t('templates.namePlaceholder'), 'aria-label': t('templates.name') });
  const list = el('div', { class: 'saved-list' });
  const note = el('span', { class: 'muted' });

  const draw = (current: readonly TemplateRow[]) => {
    replace(
      list,
      current.length === 0
        ? el('p', { class: 'muted' }, t('templates.empty'))
        : el(
            'ul',
            { class: 'saved' },
            ...current.map((row) =>
              el(
                'li',
                {},
                el('span', { class: 'saved-name' }, row.name),
                el(
                  'button',
                  {
                    type: 'button',
                    class: 'link-button',
                    onclick: () => {
                      // Asked, because it replaces something somebody may have
                      // been in the middle of writing — but only when there is
                      // in fact something to lose.
                      const occupied = currentBody().trim() !== '' && currentBody() !== row.body;
                      if (occupied && !window.confirm(t('templates.confirmUse', { name: row.name }))) return;
                      use(row.body);
                    },
                  },
                  t('templates.use'),
                ),
                el(
                  'button',
                  {
                    type: 'button',
                    class: 'link-button',
                    onclick: () => {
                      if (!window.confirm(t('templates.confirmDelete', { name: row.name }))) return;
                      void deleteTemplate(row.id)
                        .then(() => savedTexts())
                        .then(draw)
                        .catch(() => {
                          note.textContent = t('app.loadFailed');
                        });
                    },
                  },
                  t('templates.delete'),
                ),
              ),
            ),
          ),
    );
  };

  const save = el('button', { type: 'button', class: 'secondary' }, t('templates.save'));
  save.addEventListener('click', () => {
    const body = currentBody();
    if (body.trim() === '') {
      note.textContent = t('templates.bodyRequired');
      return;
    }
    if (name.value.trim() === '') {
      note.textContent = t('templates.nameRequired');
      name.focus();
      return;
    }

    save.disabled = true;
    void saveTemplate(name.value.trim(), body)
      .then(() => savedTexts())
      .then((current) => {
        draw(current);
        name.value = '';
        note.textContent = t('templates.saved');
      })
      .catch(() => {
        note.textContent = t('app.loadFailed');
      })
      .finally(() => {
        save.disabled = false;
      });
  });

  replace(
    panel,
    el('h3', {}, t('templates.title')),
    el('div', { class: 'saved-controls' }, name, save, note),
    list,
  );
  draw(rows);
}

// ---------------------------------------------------------------------------
// Choosing a mode

/**
 * The format is never detected automatically, and the hint says so.
 *
 * All three panels keep whatever was pasted into them, so text sits in two of
 * them at once routinely — and `recipientPairs` reads the selected mode and
 * only the selected mode. Guessing which one was meant is the one mistake this
 * screen must not make.
 *
 * **Built once, and marked afterwards.** It used to be drawn from `input` and
 * then never drawn again, so pressing a tab changed the mode, redrew the panel,
 * and left the strip showing the tab that was current when the screen opened.
 * Redrawing it on every `refresh` would fix the mark and break the keyboard —
 * an arrow key selects the next radio, which calls `set`, which would replace
 * the radio that has the focus. So the nodes are kept and `showMode` moves the
 * `checked`, the `aria-current` and the hint between them.
 */
function modeTabs(set: (over: Partial<CampaignInput>) => void): {
  readonly node: Child;
  readonly showMode: (mode: InputMode) => void;
} {
  const hint = el('p', { class: 'hint' });

  // Real radios rather than styled divs, so the strip is reachable and
  // traversable with the arrow keys — the same arrangement the old panel used,
  // minus the `:checked` selector that made it work without script.
  const tabs = MODE_LABELS.map(([mode, label]) => {
    const radio = el('input', {
      class: 'mode-radio',
      type: 'radio',
      name: 'mode',
      value: mode,
      onchange: () => set({ mode }),
    });
    return { mode, radio, tab: el('label', {}, radio, t(label as never)) };
  });

  const node = el(
    'fieldset',
    { class: 'modes' },
    el('legend', {}, t('campaign.mode.legend')),
    el('p', { class: 'muted' }, t('campaign.mode.hint')),
    // A fieldset with a legend and three radios is already a named group of
    // choices. `role="tablist"` was on this div and said it was something else,
    // without a single `role="tab"` under it to make that true.
    el('div', { class: 'tablist' }, ...tabs.map((one) => one.tab)),
    hint,
  );

  const showMode = (mode: InputMode) => {
    for (const one of tabs) {
      // `checked` the property, not the attribute: the attribute is only the
      // *initial* state, and setting it on a radio the browser has already
      // taken a value from changes nothing anybody can see.
      one.radio.checked = one.mode === mode;
      if (one.mode === mode) one.tab.setAttribute('aria-current', 'page');
      else one.tab.removeAttribute('aria-current');
    }
    replace(hint, t(hintFor(mode) as never));
  };

  return { node, showMode };
}

/** The one sentence under the strip changes with the tab; the rest does not. */
function hintFor(mode: InputMode): string {
  return MODE_LABELS.find(([name]) => name === mode)?.[2] ?? 'campaign.mode.hint';
}

/** The two boxes of mode B and the one thing that may write to them. */
interface Blocks {
  readonly phones: HTMLTextAreaElement;
  readonly codes: HTMLTextAreaElement;
  readonly swap: () => void;
}

/**
 * One input mode's panel: a node built once, and the way to bring what is
 * derived from `input` up to date inside it.
 *
 * The two halves are separate because only the second may run on a keystroke.
 * The node holds the things somebody is in the middle of using — the textareas,
 * the separator, the file chooser — and moving any of those loses the focus and
 * the caret. `update` touches counters, findings and previews, none of which
 * can hold either.
 */
interface Panel {
  readonly node: HTMLElement;
  readonly update: (input: CampaignInput) => void;
}

// ---------------------------------------------------------------------------
// Mode A — paired lines

function pairsPanel(
  set: (over: Partial<CampaignInput>) => void,
  pairs: HTMLTextAreaElement,
): Panel {
  // Built once, like the box above it. A `<select>` rebuilt by the change it
  // just reported is a `<select>` that loses the focus, and the keyboard with
  // it — so the mark is moved by assigning `value`, the property, rather than
  // by drawing a fresh `selected` attribute onto a fresh set of options.
  const separator = el(
    'select',
    { onchange: (event: Event) => set({ separator: (event.target as HTMLSelectElement).value as SeparatorName }) },
    ...(Object.keys(SEPARATORS) as SeparatorName[]).map((name) =>
      el('option', { value: name }, t(SEPARATOR_LABELS[name] as never)),
    ),
  );

  const node = el(
    'div',
    {},
    el('label', {}, t('campaign.pairs.label')),
    el('p', { class: 'muted' }, t('campaign.pairs.help')),
    pairs,
    el('label', { class: 'separator' }, t('campaign.separator.label'), separator),
  );

  return {
    node,
    update: (input) => {
      // The box is the state, so this matters only if something other than
      // typing ever changes the field. Guarded, because assigning `value` is
      // what puts the caret at the end.
      if (pairs.value !== input.pairs) pairs.value = input.pairs;
      if (separator.value !== input.separator) separator.value = input.separator;
    },
  };
}

// ---------------------------------------------------------------------------
// Mode B — two blocks

/**
 * Two boxes, side by side, and the counters under them.
 *
 * Side by side because that is what the mode means: line N on the left goes to
 * line N on the right. One box with a blank line in the middle said the same
 * thing in a sentence, and the sentence was the part that went wrong — a list
 * long enough to scroll puts the boundary off the screen, and a stray line
 * pasted above it shifts every pair below without changing anything visible.
 *
 * Neither box is written to here, and neither is moved. They hold their own
 * text (`renderNewCampaign` says why), they stay where they were put, and
 * `input.blocks` is the two of them joined — which is all `update` reads.
 */
function blocksPanel(blocks: Blocks): Panel {
  // The counters are the whole point of this mode: one extra line shifts every
  // pair below it, and the only way to notice is that the two numbers stop
  // matching. These three are what a keystroke redraws; the boxes above them
  // are not.
  const counters = el('div', { class: 'counters' });
  const findings = el('div', { class: 'findings' });
  const rows = el('div', {});

  const node = el(
    'div',
    {},
    el('p', { class: 'muted' }, t('campaign.blocks.help')),
    // Named as a group, because the two boxes only mean anything together: a
    // screen reader landing in the second one should be told it is the second
    // of a pair rather than a lone box called "Promo codes".
    el(
      'div',
      { class: 'blocks-columns', role: 'group', 'aria-label': t('campaign.blocks.label') },
      column(t('campaign.blocks.phonesLabel'), blocks.phones),
      column(t('campaign.blocks.codesLabel'), blocks.codes),
    ),
    counters,
    findings,
    rows,
  );

  return {
    node,
    update: (input) => {
      const view = blocksView(input.blocks);

      counters.className = view.diverged ? 'counters is-bad' : 'counters';
      replace(
        counters,
        el('span', {}, `${t('campaign.blocks.phones')}: ${number(view.phoneCount)}`),
        el('span', {}, `${t('campaign.blocks.codes')}: ${number(view.codeCount)}`),
      );
      replace(
        findings,
        ...view.findings.map((found) =>
          finding(BLOCK_WORDING[found.name], found.params, swapper(view.swapped, found.name, blocks.swap)),
        ),
      );
      replace(rows, preview(view.rows));
    },
  };
}

/** A heading and the box under it, as one column of the pair. */
function column(label: string, box: HTMLTextAreaElement): Child {
  return el('div', { class: 'blocks-column' }, el('label', {}, label), box);
}

/**
 * The one finding with a fix attached, and only when there is one to attach.
 *
 * `swapped` is not used as text any more — `swap` exchanges the two boxes — but
 * it is still the panel's answer to *whether a swap is worth offering*: it
 * exists only when there really are two blocks the wrong way round. So a button
 * that would do nothing is never drawn.
 */
function swapper(
  swapped: string | undefined,
  name: string,
  swap: () => void,
): (() => void) | undefined {
  if (name !== 'blocks-reversed' || swapped === undefined) return undefined;
  return swap;
}

function preview(rows: ReturnType<typeof blocksView>['rows']): Child {
  if (rows.length === 0) return false;

  return el(
    'table',
    { class: 'preview' },
    el(
      'thead',
      {},
      el('tr', {}, el('th', {}, t('campaign.preview.row')), el('th', {}, t('campaign.preview.phone')), el('th', {}, t('campaign.preview.code'))),
    ),
    el(
      'tbody',
      {},
      ...rows.map((row) =>
        row.kind === 'gap'
          ? el('tr', { class: 'preview-gap' }, el('td', { colspan: 3 }, t('campaign.preview.more', { count: row.hidden })))
          : el(
              'tr',
              { class: row.kind === 'unpaired' ? 'is-unpaired' : undefined },
              el('td', { class: 'numeric' }, row.line),
              el('td', {}, row.phone ?? t('campaign.preview.unpaired')),
              el('td', {}, row.code ?? t('campaign.preview.unpaired')),
            ),
      ),
    ),
  );
}

// ---------------------------------------------------------------------------
// Mode C — a file

/**
 * The file is read in the browser and never uploaded (PLAN §9).
 *
 * Ten thousand customer phone numbers do not need to travel anywhere to be
 * counted, and a file that never leaves the machine is a file that cannot be
 * left in a temporary directory on a server.
 */
function filePanel(
  set: (over: Partial<CampaignInput>) => void,
  maxRows: number,
): Panel {
  const chooser = el('input', { type: 'file', accept: '.csv,.xlsx,text/csv' });
  const pending = el('div', { class: 'import-pending' });
  const chosen = el('p', { class: 'import-name', hidden: true });
  const figures = el('div', { class: 'figures' });
  const findings = el('div', { class: 'findings' });

  // What `update` last saw. The chooser's handler needs the list that is
  // already loaded, and it runs long after the redraw that knew about it.
  let current: CampaignInput = EMPTY_INPUT;

  chooser.addEventListener('change', () => {
    const file = chooser.files?.[0];
    if (file === undefined) return;

    const existing = readImported(current.imported);

    void readSpreadsheet(file, file.name).then((read) => {
      const incoming = importView(read.pairs, {
        maxRows,
        ...(read.problem === undefined ? {} : { problem: read.problem }),
      });

      if (!incoming.accepted) {
        // Refused whole, never trimmed: a list cut down to fit would send a
        // campaign that looks complete and is not.
        replace(pending, ...incoming.findings.map((found) => finding(IMPORT_WORDING[found.name], found.params)));
        return;
      }

      if (existing.length === 0) {
        replace(pending);
        set({ imported: writeImported(read.pairs), importName: file.name });
        return;
      }

      // Neither answer is safe to assume: replacing silently throws away a list
      // somebody spent a morning on, appending silently doubles a campaign when
      // the same file is dropped twice. So the panel asks.
      replace(
        pending,
        el('p', {}, t('campaign.import.combine', { existing: existing.length, incoming: read.pairs.length })),
        el(
          'div',
          { class: 'actions' },
          ...(['replace', 'append'] as const).map((how) =>
            el(
              'button',
              {
                type: 'button',
                class: how === 'append' ? undefined : 'secondary',
                onclick: () => {
                  // Answered, so it stops being asked. The panel used to be
                  // rebuilt on the next keystroke, which cleared this by
                  // accident — and cleared a refusal nobody had finished
                  // reading along with it.
                  replace(pending);
                  set({
                    imported: writeImported(combineImports(existing, read.pairs, how)),
                    importName: file.name,
                  });
                },
              },
              t(how === 'append' ? 'campaign.import.append' : 'campaign.import.replace'),
            ),
          ),
        ),
      );
    });
  });

  const node = el(
    'div',
    {},
    el('p', { class: 'muted' }, t('campaign.mode.file.hint')),
    chooser,
    pending,
    chosen,
    figures,
    findings,
  );

  return {
    node,
    update: (input) => {
      current = input;
      const view = importView(readImported(input.imported), { maxRows });

      chosen.textContent = input.importName;
      chosen.hidden = input.importName === '';

      replace(
        figures,
        el('p', { class: 'figure' }, el('output', {}, number(view.totals.imported)), el('span', {}, t('campaign.import.imported'))),
        el('p', { class: 'figure' }, el('output', {}, number(view.totals.valid)), el('span', {}, t('campaign.import.valid'))),
        el('p', { class: 'figure' }, el('output', {}, number(view.totals.invalid)), el('span', {}, t('campaign.import.invalid'))),
        el('p', { class: 'figure' }, el('output', {}, number(view.totals.duplicate)), el('span', {}, t('campaign.import.duplicate'))),
      );
      replace(findings, ...view.findings.map((found) => finding(IMPORT_WORDING[found.name], found.params)));
    },
  };
}

// ---------------------------------------------------------------------------
// The message

function templateCounters(input: CampaignInput, check: CampaignCheck): readonly Child[] {
  const view = templateView(input.template, check.outcome.recipients.map((row) => row.promoCode), {
    warnIfPartsAbove: WARN_IF_PARTS_ABOVE,
  });
  if (!view.typed) return [];

  return [
    el(
      'div',
      { class: view.overThreshold ? 'counters is-warn' : 'counters' },
      el('span', {}, `${t('campaign.template.characters')}: ${number(view.length)} / ${number(view.capacity)}`),
      el('span', {}, `${t('campaign.template.parts')}: ${number(view.parts)}`),
      el('span', { class: 'encoding' }, view.encoding),
    ),
    // How much room is left before the *next* part — but not once the message
    // is already over the threshold, because at that point it is not the
    // question anybody is asking.
    view.overThreshold
      ? false
      : el('p', { class: 'headroom' }, t('campaign.template.headroom', { remaining: view.remaining, next: view.parts + 1 })),
    ...view.findings.map((found) =>
      found.name === 'template-multi-segment'
        ? finding({ message: 'campaign.template.multiSegment', level: 'warning' }, found.params)
        : finding(
            { message: CAMPAIGN_PROBLEM_MESSAGES[found.name], level: 'error' },
            { ...found.params, placeholder: PROMO_PLACEHOLDER },
          ),
    ),
  ];
}

// ---------------------------------------------------------------------------
// The list review

function review(
  input: CampaignInput,
  set: (over: Partial<CampaignInput>) => void,
  check: CampaignCheck,
  maxRows: number,
): readonly Child[] {
  const view = listView(input, { maxRows }, check);
  const { totals } = view;

  return [
    el(
      'div',
      { class: 'figures' },
      el('p', { class: 'figure' }, el('output', {}, number(totals.imported)), el('span', {}, t('campaign.import.imported'))),
      el('p', { class: 'figure' }, el('output', {}, number(totals.valid)), el('span', {}, t('campaign.import.valid'))),
      el('p', { class: 'figure' }, el('output', {}, number(totals.invalid)), el('span', {}, t('campaign.import.invalid'))),
      el('p', { class: 'figure' }, el('output', {}, number(totals.duplicate)), el('span', {}, t('campaign.import.duplicate'))),
    ),
    problemTable(view.problems),
    ...duplicates(view, input, set),
  ];
}

/**
 * Rows that will not be sent — shown, never dropped.
 *
 * A list that silently shrank from 150 to 148 is the worst possible outcome
 * (§59), and none of these block anything: fixing three bad numbers out of ten
 * thousand is the administrator's call, not the panel's.
 */
function problemTable(rows: ReturnType<typeof listView>['problems']): Child {
  if (rows.length === 0) return false;

  return el(
    'details',
    { class: 'problems', open: true },
    el('summary', {}, t('campaign.review.problems')),
    el(
      'table',
      { class: 'preview' },
      el(
        'thead',
        {},
        el(
          'tr',
          {},
          el('th', {}, t('campaign.preview.line')),
          el('th', {}, t('campaign.preview.phone')),
          el('th', {}, t('campaign.preview.code')),
          el('th', {}, t('campaign.preview.reason')),
        ),
      ),
      el(
        'tbody',
        {},
        ...rows.map((row) =>
          row.kind === 'gap'
            ? el('tr', { class: 'preview-gap' }, el('td', { colspan: 4 }, t('campaign.preview.more', { count: row.hidden })))
            : el(
                'tr',
                {},
                el('td', { class: 'numeric' }, row.line),
                el('td', {}, row.phone),
                el('td', {}, row.code),
                el('td', {}, translateKey(PROBLEM_MESSAGE_KEYS[row.problem], row.params)),
              ),
        ),
      ),
    ),
  );
}

/**
 * Repeated numbers, and the one question this screen refuses to guess at.
 *
 * Both answers are wrong sometimes — a list pasted twice wants the first row, a
 * corrected row appended at the bottom wants the last — so guessing would
 * silently send the wrong promo code to a real customer. This is the only
 * finding on the screen that blocks Continue without anything being *wrong*.
 */
function duplicates(
  view: ReturnType<typeof listView>,
  input: CampaignInput,
  set: (over: Partial<CampaignInput>) => void,
): readonly Child[] {
  if (view.duplicateCount === 0) return [];

  const keep = new Set(input.keep);

  return [
    el('h3', {}, t('campaign.review.duplicates')),
    el('p', { class: 'muted' }, t('campaign.review.duplicatesHint')),
    el(
      'div',
      { class: 'choices' },
      ...DUPLICATE_RESOLUTIONS.map((name) =>
        el(
          'label',
          { class: 'choice' },
          el('input', {
            type: 'radio',
            name: 'duplicates',
            value: name,
            checked: input.duplicates === name,
            onchange: () => set({ duplicates: name as DuplicateResolution }),
          }),
          t(`campaign.review.keep${name[0]?.toUpperCase()}${name.slice(1)}` as never),
        ),
      ),
    ),
    ...view.duplicates.map((group) =>
      group.kind === 'gap'
        ? el('p', { class: 'muted' }, t('campaign.review.moreGroups', { count: group.hidden }))
        : el(
            'table',
            { class: 'preview' },
            el(
              'tbody',
              {},
              ...group.rows.map((row) =>
                el(
                  'tr',
                  { class: row.kept ? 'is-kept' : undefined },
                  el(
                    'td',
                    {},
                    // Only offered under `manual`: under first/last the choice
                    // is already made for every group at once, and a radio that
                    // does nothing is worse than no radio.
                    input.duplicates === 'manual'
                      ? el('input', {
                          type: 'radio',
                          name: `keep-${group.index}`,
                          checked: keep.has(row.line),
                          'aria-label': t('campaign.review.keep'),
                          onchange: () =>
                            set({
                              keep: [
                                ...input.keep.filter((line) => !group.rows.some((other) => other.line === line)),
                                row.line,
                              ],
                            }),
                        })
                      : false,
                  ),
                  el('td', { class: 'numeric' }, row.line),
                  el('td', {}, row.phone),
                  el('td', {}, row.code),
                ),
              ),
            ),
          ),
    ),
  ];
}

// ---------------------------------------------------------------------------

/**
 * One finding: a sentence, its level, and sometimes a button that fixes it.
 *
 * The level is what tells a refusal from a question. §59 is explicit that the
 * administrator sees the truth and decides, so most of what this screen has to
 * say is a warning with the numbers in it rather than a wall.
 */
function finding(
  wording: Wording,
  params: Readonly<Record<string, string | number>>,
  act?: () => void,
): Child {
  return el(
    'div',
    { class: `finding ${wording.level}` },
    el('span', {}, translateKey(wording.message, params)),
    act === undefined || wording.action === undefined
      ? false
      : el('button', { type: 'button', class: 'secondary', onclick: act }, translateKey(wording.action)),
  );
}
